import React from 'react'
import { Container, Header, Divider } from 'semantic-ui-react'

import SEO from 'lib/components/SEO'

import SpeedGraph from './SpeedGraph'
import CTA from './CTA'
import Section from './Section'

const Children = ({}) => {
  return (
    <>
      <SEO
        title="Recalled products affect children and infants | Recall Monitor"
        description="Learn how children and infants are at risk from recalled products in Canada. More than 40 products affecting children were recalled in 2019. Read about the most common causes and dangers."
        canonical="https://recallmonitor.ca/why/children"
        type="article"
        image="https://recallmonitor.ca/static/why/crib.jpg"
      />
      <Container text css={{ margin: '1rem 0' }}>
        <Header as="h1">
          Children and Infants
          <Header.Subheader>
            Keep your children and babies safe from harmful products
          </Header.Subheader>
        </Header>
        <p>
          Every year hundreds of products are recalled that affect children and
          babies. Since 2019 there were <strong>75</strong> products that can
          put the safety of your child or baby at risk. That is a product recall
          every <strong>6 days</strong>.
        </p>

        <p>
          How can parents keep their children and infants protected? How can
          schools, kindergartens and other institutions keep your children safe?{' '}
          <strong>
            Just a few hours of delay might mean unknown exposure to these
            harmful products.
          </strong>
        </p>

        <SpeedGraph />

        <p>
          Recall Monitor can help you keep track of active recalls for products
          that affect children.
        </p>

        <p>
          Here are some examples of product recalls that posed a threat to the
          health and safety of children and infants since 2019.
        </p>

        <Divider />

        <Section img="/static/why/flammable.jpg">
          <Header>Flammability Hazard</Header>
          <p>
            <strong>17</strong> products were recalled because they were a
            flammability hazard.
          </p>
          <p>
            Flammable clothing is clothing that catches fire and burns easily.
            If clothing catches fire while being worn, the wearer may suffer
            serious burns which can lead to permanent scarring, disability and
            even death. Children's clothing that most commonly catch fire are
            pajamas and nighties. Sometimes, an open fire is not even necessary,
            even being close to heat sources can be dangerous.
          </p>
        </Section>

        <Divider />

        <Section img="/static/why/pacifier.jpeg" reversed>
          <Header>Choking Hazard</Header>
          <p>
            <strong>24</strong> children's products posed the serious risk of
            choking.
          </p>
          <p>
            Choking hazards occur when faulty products break or snap to create
            small parts that children may choke on. Examples of such products
            are things like pacifiers and baby bottles. Parts of such recalled
            products can break and get stuck in a child's throat, blocking their
            airways.
          </p>
        </Section>

        <Divider />

        <Section img="/static/why/necklace.jpg">
          <Header>Hazardous Metals</Header>
          <p>
            We saw <strong>8</strong> products that contained excess hazardous
            metals, beyond allowable limits for consumers.
          </p>
          <p>
            These products usually contain lead, cadmium or other harmful metals
            or materials which can harm your child in the short and long term.
          </p>
          <p>
            We've seen necklaces, bracelets, accessories and even a chair
            playset containing levels of lead in excess of allowable limits.
          </p>
        </Section>

        <Divider />

        <Section img="/static/why/fall.jpg" reversed>
          <Header>Fall Hazard</Header>
          <p>
            There were <strong>5</strong> products recalled because of fall
            hazards. Faulty strollers, high chairs, shoes and accessories may in
            some cases break, disassemble and cause falls resulting in injuries.
          </p>
        </Section>

        <Divider />

        <Section img="/static/why/crib.jpg">
          <Header>Other Hazards</Header>
          <p>
            This year there were <strong>22</strong> other products recalled
            that could cause strangulation, laceration and grave injuries.
          </p>
          <p>
            Cribs and sleepers might be poorly constructed, causing children to
            fall or get trapped in parts of them. Coats, raincoats, hoodies may
            have strings and chains on them which - if there's a fault - might
            result in strangulation.
          </p>
          <p>
            In Canada, we enjoy a very high standard of product quality
            especially when it comes to children. Even so, some products that
            make it through the system are later recalled for not having
            adequate proof of safety.
          </p>
          <p>
            Tragically, there were <strong>2</strong> products recalled this
            year because of reports of deaths in the US.
          </p>
        </Section>

        <Divider />

        <Header>The list goes on...</Header>
        <p>
          Were you aware of any products recalled because of safety concerns to
          infants and children?
        </p>
        <p>
          Recall Monitor will help you get knowledgeable about recalled
          products. Don't be at the mercy of a news segment you may never see.
          Sign up now, and start receiving the alerts you care about -
          instantly.
        </p>
        <CTA />
      </Container>
    </>
  )
}

export default Children
